/**
 * Routes. Everything inside a workspace lives under /w/:ws, so the workspace
 * survives a reload through the URL rather than browser storage.
 */
import { useEffect } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useParams } from "react-router";
import { Shell } from "./components/Shell.tsx";
import { ErrorBlock, Loading } from "./components/ui.tsx";
import { setLocale, t } from "./i18n/index.ts";
import { SignedOutError } from "./lib/api.ts";
import { useMe, useWorkspace } from "./lib/queries.ts";
import { WorkspaceContext } from "./lib/workspace.tsx";
import { Dashboard } from "./pages/Dashboard.tsx";
import { Finance } from "./pages/Finance.tsx";
import { Inventory } from "./pages/Inventory.tsx";
import { ItemPage } from "./pages/item/ItemPage.tsx";
import { NewItem } from "./pages/NewItem.tsx";
import { Onboarding } from "./pages/Onboarding.tsx";
import { Sales } from "./pages/Sales.tsx";
import { Settings } from "./pages/Settings.tsx";
import { ShopReport } from "./pages/ShopReport.tsx";
import { Shops } from "./pages/Shops.tsx";
import { Welcome } from "./pages/Welcome.tsx";

function Home() {
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (me.isError) return <div className="main"><ErrorBlock error={me.error} onRetry={() => void me.refetch()} /></div>;
  const first = me.data.workspaces[0];
  return <Navigate to={first ? `/w/${first.id}/dashboard` : "/new-workspace"} replace />;
}

function NotFound() {
  return (
    <div className="center-page">
      <div className="welcome">
        <h1>{t("notFound.title")}</h1>
        <Link className="btn btn-primary" to="/">{t("notFound.home")}</Link>
      </div>
    </div>
  );
}

function WorkspaceRoutes() {
  const { ws = "" } = useParams();
  const workspace = useWorkspace(ws);
  useEffect(() => { if (workspace.data) setLocale(workspace.data.locale); }, [workspace.data]);
  if (workspace.isPending) return <Loading />;
  if (workspace.isError) {
    if (workspace.error instanceof SignedOutError) return <Welcome />;
    return <NotFound />;
  }
  return (
    <WorkspaceContext.Provider value={workspace.data}>
      <Shell>
        <Routes>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="items/new" element={<NewItem />} />
          <Route path="items/:id" element={<ItemPage />} />
          {workspace.data.permissions.includes("sales.write") && <Route path="sales" element={<Sales />} />}
          {workspace.data.permissions.includes("finance.read") && <Route path="finance" element={<Finance />} />}
          {workspace.data.permissions.includes("listings.write") && <Route path="shops" element={<Shops />} />}
          {workspace.data.permissions.includes("listings.write") && <Route path="shops/report" element={<ShopReport />} />}
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Routes>
      </Shell>
    </WorkspaceContext.Provider>
  );
}

export function App() {
  const me = useMe();
  if (me.isPending) return <div className="center-page"><div className="spinner" /></div>;
  if (me.isError && me.error instanceof SignedOutError) return <Welcome />;
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/new-workspace" element={<Onboarding />} />
        <Route path="/w/:ws/*" element={<WorkspaceRoutes />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
